import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Collapse from '@mui/material/Collapse';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { styled, alpha } from '@mui/material/styles';
import { NavSubTree } from './MenuStyles';

const SubMenuButton = styled(ListItemButton, {
    shouldForwardProp: (prop) => prop !== 'sidebarOpen' && prop !== 'isOpen',
})(({ theme, sidebarOpen }) => ({
    position: 'relative',
    transition: 'all 0.18s ease',
    boxSizing: 'border-box',

    ...(sidebarOpen ? {
        display: 'flex',
        alignItems: 'center',
        borderRadius: '6px',
        margin: '1px 0',
        padding: '6.5px 8px !important',
        minHeight: '34px',
        color: theme.palette.text.primary,
        '&:hover': {
            backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.06)
                : alpha(theme.palette.common.black, 0.04),
        },
        '& .MuiListItemIcon-root': {
            minWidth: 'unset !important',
            width: '19px !important',
            flex: '0 0 19px !important',
            color: theme.palette.mode === 'dark' ? '#90caf9' : '#5B8CC4',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '10px !important',
            '& svg': {
                fontSize: '19px !important',
            },
        },
        '& .MuiTypography-root, & .MuiListItemText-primary': {
            fontSize: '13.5px !important',
            fontWeight: 400,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: 'inherit',
            lineHeight: 1.4,
        },
    } : {
        // Collapsed rail mode
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '8px',
        width: '44px',
        height: '44px',
        minWidth: '44px',
        maxWidth: '44px',
        margin: '4px auto',
        padding: '0 !important',
        overflow: 'hidden',
        color: theme.palette.mode === 'dark' ? '#90caf9' : '#4b78b4',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
        '& .MuiListItemIcon-root': {
            minWidth: 'unset !important',
            width: '100% !important',
            margin: '0 !important',
            padding: '0 !important',
            color: 'inherit',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            '& svg': {
                fontSize: '1.35rem',
            },
        },
        '& .MuiTypography-root, & .MuiListItemText-root': {
            display: 'none !important',
        },
    }),
}));

const SubMenu = ({
    currentItem,
    item,
    title,
    icon,
    children,
    sidebarOpen,
    onClick,
    badge,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const isOpen = currentItem === item;

    const handleClick = (e) => {
        if (!sidebarOpen) {
            setAnchorEl(e.currentTarget);
            return;
        }

        if (onClick) {
            onClick(item);
        }
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    if (!sidebarOpen) {
        return (
            <>
                <Tooltip title={title} placement="right">
                    <SubMenuButton
                        sidebarOpen={false}
                        aria-haspopup="true"
                        onClick={handleClick}
                    >
                        {icon ? <ListItemIcon>{icon}</ListItemIcon> : null}
                    </SubMenuButton>
                </Tooltip>

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{
                        sx: {
                            minWidth: 180,
                            borderRadius: '8px',
                            boxShadow: (theme) => theme.shadows[3],
                            p: 0.5,
                        },
                    }}
                    onClose={handleCloseMenu}
                >
                    <div role="presentation" onClick={handleCloseMenu}>
                        {children}
                    </div>
                </Menu>
            </>
        );
    }

    return (
        <div>
            <SubMenuButton
                sidebarOpen
                isOpen={isOpen}
                onClick={handleClick}
            >
                {icon ? <ListItemIcon>{icon}</ListItemIcon> : null}
                <ListItemText
                    primary={title}
                    sx={{ flex: 1, minWidth: 0, m: 0 }}
                />
                {badge ? (
                    <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                        {badge}
                    </Box>
                ) : null}
                <KeyboardArrowDownIcon
                    sx={{
                        fontSize: '16px',
                        color: 'text.secondary',
                        ml: badge ? 0 : 'auto',
                        transition: 'transform 0.2s ease',
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                />
            </SubMenuButton>

            <Collapse unmountOnExit in={isOpen} timeout="auto">
                <NavSubTree>
                    {children}
                </NavSubTree>
            </Collapse>
        </div>
    );
};

SubMenu.propTypes = {
    currentItem: PropTypes.string,
    item: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    icon: PropTypes.node,
    children: PropTypes.node,
    sidebarOpen: PropTypes.bool,
    onClick: PropTypes.func,
    badge: PropTypes.node,
};

export default SubMenu;
